<script setup lang="ts">
/**
 * "#" column cell for the client card visits table. Renders the 1-based
 * visit number for `lesson` rows so the user can see at a glance which
 * appointment in the client's history each lesson represents. Non-lesson
 * rows (payments) leave this column blank because the number does not
 * apply to them — monthly-balance and disease rows never reach this cell
 * because `DynamicTable.rowOverride` collapses them into a single full-row
 * cell.
 *
 * The numeric value itself is computed in `ClientCard.vue` from the loaded
 * lessons and the client's `business_service_count`. When the total is
 * known, the newest loaded lesson carries the largest visit number; older
 * lessons decrement from there. When the count is unavailable, the parent
 * falls back to the lesson's position in the loaded set so the column
 * still renders meaningful sequencing for the data on screen.
 */
const props = defineProps<{
  /**
   * 1-based visit number for this row, or `undefined` for non-lesson rows
   * (payments). `undefined` renders an empty cell so the column visually
   * collapses to whitespace on those rows.
   */
  visitNumber?: number
}>()
</script>

<template>
  <td class="visit-number-cell font-tufte">
    <template v-if="props.visitNumber !== undefined">{{ props.visitNumber }}</template>
  </td>
</template>

<style scoped>
.visit-number-cell {
  /* Inherits the Tufte body serif and `--text-tufte-xs` font-size from the
   * surrounding table so the digit weight visually lines up with the date
   * column. The muted text color demotes the index to secondary
   * information so the eye reads `Date → Type → Δ → Bal.` first, with
   * the per-row index sitting alongside as a soft reference. */
  text-align: right;
  white-space: nowrap;
  color: var(--color-tufte-muted);
}
</style>
